/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The component for Dashboard routing
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.9.0
 * @since 1.2.0
 */

import intersection from 'lodash/intersection';
import map from 'lodash/map';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, Switch } from 'react-router';
import { createStructuredSelector } from 'reselect';

import { AccessComponentKeyEnum } from '../../modules/role/constants/guard/guardConstants';
import CorporatePage from '../../pages/CorporatePage/CorporatePage';
import PageNotFound from '../../pages/ErrorPage/PageNotFound';
import { AuthorityRoleEnum, SubscriberTypeEnum } from '../../pages/PageAuth/constants/authConstants';
import {
    makeSelectAuthAuthorities,
    makeSelectIsDashboardAvailable,
    makeSelectIsSingleAccount,
    makeSelectIsSubscriber,
    makeSelectSubscriberType,
} from '../../pages/PageAuth/selectors/authSelectors';
import { IAuthority } from '../../pages/PageAuth/types/authTypes';
import {
    makeSelectorWidgetEmptyList,
    makeSelectorWidgetMetaList,
} from '../../pages/PageDashboard/components/Widgets/widgetSelectors';
import { TWidgetMetaList } from '../../pages/PageDashboard/components/Widgets/widgetTypes';
import PageDashboard from '../../pages/PageDashboard/Loadable';
import { PageDashboardContainerStyled } from '../../pages/PageDashboard/styled/PageDashboardStyled';
import { IRootReducer } from '../../reducers/rootReducer';
import { RoutePath } from '../constants/routesConstants';
import PrivateRoute from '../PrivateRoute';

export const pageDashboardRedirectLogic = (authorities: IAuthority[], subscriberType: string | null) => {
    const notAllowedRoles = [
        AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE,
        AuthorityRoleEnum.ROLE_SUBSCRIBER_MANAGE_RATEPLAN,
        AuthorityRoleEnum.ROLE_SUBSCRIBER_READ_ONLY,
    ];

    const isNotAllowedByRole = intersection(
        map(authorities, 'authority'),
        notAllowedRoles,
    ).length > 0;

    if (isNotAllowedByRole) {
        switch (subscriberType) {
            case SubscriberTypeEnum.FIXED:
                return <Redirect to={RoutePath.subscriberFixed}/>;
            case SubscriberTypeEnum.MOBILE:
                return <Redirect to={RoutePath.subscriberMobile}/>;
            default:
                break;
        }
    }
    return <Redirect to={RoutePath.account}/>;
};

export const DashboardRoute = (props: RouteComponentProps & IConnectedProps) => {
    if (!props.isPageDashboardActive || props.emptyWidgets || props.isSubscriber) {
        return pageDashboardRedirectLogic(props.authorities, props.subscriberType);
    } else {
        return <Switch>

            {/* Dashboard */}
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageDashboard}
                component={PageDashboard}
                path={RoutePath.dashboard}
                wrapper={CorporatePage}
                wrapperProps={({
                    render: () => <PageDashboardContainerStyled/>,
                })}
            />

            {/* PageNotFound */}
            <PrivateRoute
                accessKey={AccessComponentKeyEnum.PageNotFound}
                component={PageNotFound}
                path="**"
            />
        </Switch>;
    }
};

interface IConnectedProps {
    authorities: IAuthority[];
    emptyWidgets: boolean;
    isPageDashboardActive: boolean;
    isSingleAccount: boolean;
    isSubscriber: boolean;
    subscriberType: string | null;
    widgetMeta: TWidgetMetaList;
}

const mapStateToProps = createStructuredSelector<IRootReducer, RouteComponentProps, IConnectedProps>({
    authorities: makeSelectAuthAuthorities(),
    emptyWidgets: makeSelectorWidgetEmptyList(),
    isPageDashboardActive: makeSelectIsDashboardAvailable(),
    isSingleAccount: makeSelectIsSingleAccount(),
    isSubscriber: makeSelectIsSubscriber(),
    subscriberType: makeSelectSubscriberType(),
    widgetMeta: makeSelectorWidgetMetaList(),
});

export default connect(mapStateToProps)(DashboardRoute);
